// GAVION ai-email: the real AiApi (edge functions gavion-ai-draft / gavion-ai-style; RPCs ai_reject_draft and
// ai_save_my_style). The API key never reaches the browser: only the edge holds it. Every failure is an AiApiError
// with a stable code (a raw HTTP status never escapes, so a 401/403 never logs the user out).
import type { SupabaseLike } from "../../integrations/api/supabaseM365Api";
import {
  AiApiError,
  callBriefRowOf,
  type AiApi,
  type AiDraftResult,
  type CallBriefRequestResult,
} from "./types";

type AiClient = Pick<SupabaseLike, "functions" | "rpc">;
const SAFE = /^[a-z0-9_]{1,40}$/;
const code = (v: unknown) =>
  typeof v === "string" && SAFE.test(v) ? v : "ai_unavailable";
/** The guard's blocked figures/words: short strings only, at most 5 (never a draft body). */
const detailOf = (v: unknown): string[] =>
  Array.isArray(v)
    ? v
        .filter(
          (x): x is string =>
            typeof x === "string" && x.trim() !== "" && x.length <= 40,
        )
        .slice(0, 5)
    : [];

async function invoke<T>(c: AiClient, name: string, body: unknown): Promise<T> {
  let r: { data: unknown; error: unknown };
  try {
    r = await c.functions.invoke(name, { method: "POST", body });
  } catch {
    throw new AiApiError("ai_unavailable");
  }
  if (!r.error) return r.data as T;
  const res = (r.error as { context?: unknown } | null)?.context;
  if (!(res instanceof Response)) throw new AiApiError("ai_unavailable");
  const b = (await res.json().catch(() => ({}))) as {
    error?: unknown;
    detail?: unknown;
  };
  const c2 = code(b?.error);
  throw new AiApiError(
    c2,
    c2 === "ai_unsupported_claim" ? detailOf(b?.detail) : [],
  );
}

async function rpc<T = void>(
  c: AiClient,
  name: string,
  args: Record<string, unknown>,
): Promise<T> {
  let r: Awaited<ReturnType<SupabaseLike["rpc"]>>;
  try {
    r = await c.rpc(name, args);
  } catch {
    throw new AiApiError("ai_unavailable");
  }
  if (r.error)
    throw new AiApiError(
      r.error.code === "P0001" ? code(r.error.message) : "ai_unavailable",
    );
  return r.data as T;
}

export function createSupabaseAiApi(c: AiClient): AiApi {
  return {
    async draft(i) {
      const r = await invoke<{
        draft_id: number;
        subject: string;
        body: string;
        rationale: string;
        language: "fr" | "en";
      }>(c, "gavion-ai-draft", {
        company_id: i.companyId,
        contact_id: i.contactId,
        deal_id: i.dealId,
        instruction: i.instruction.trim() || null,
        variant: i.variant,
      });
      const out: AiDraftResult = {
        draftId: r.draft_id,
        subject: r.subject,
        body: r.body,
        rationale: r.rationale,
        language: r.language,
      };
      return out;
    },
    rejectDraft: (id, reason) =>
      rpc(c, "ai_reject_draft", { p_id: id, p_reason: reason }),
    async rebuildStyle() {
      const r = await invoke<{
        status: "built" | "too_few";
        source_count: number;
      }>(c, "gavion-ai-style", {});
      return { status: r.status, sourceCount: r.source_count };
    },
    saveStyle: (profileText) =>
      rpc(c, "ai_save_my_style", { p_profile_text: profileText }),
    // GAVION ai follow-up (71 §3): « Annuler le suivi », « Décaler », « Écrire maintenant »
    cancelFollowUp: (planId) =>
      rpc(c, "ai_follow_up_cancel", { p_plan_id: planId }),
    async moveFollowUpTouch(touchId, day) {
      return String(
        await rpc<string>(c, "ai_follow_up_move", {
          p_touch_id: touchId,
          p_day: day,
        }),
      );
    },
    writeFollowUpTouchNow: (touchId) =>
      rpc(c, "ai_follow_up_write_now", { p_touch_id: touchId }),
    // GAVION call-brief-pipeline (spec A3, ruling 9): the edge gavion-call-brief; a 503 ai_not_configured hides the
    // AI lines, any other failure is « error »
    async requestCallBriefs(companyIds): Promise<CallBriefRequestResult> {
      try {
        const r = await invoke<{
          skipped?: string;
          results?: { company_id: number; status: string }[];
        } | null>(c, "gavion-call-brief", { company_ids: companyIds });
        if (r?.skipped === "disabled")
          return { outcome: "disabled", capped: [] };
        if (r?.skipped === "cap")
          return { outcome: "cap", capped: [...companyIds] };
        return {
          outcome: "ok",
          capped: (r?.results ?? [])
            .filter((x) => x.status === "cap")
            .map((x) => Number(x.company_id)),
        };
      } catch (e) {
        return {
          outcome:
            e instanceof AiApiError && e.code === "ai_not_configured"
              ? "not_configured"
              : "error",
          capped: [],
        };
      }
    },
    async callBrief(companyId) {
      return callBriefRowOf(
        await rpc<unknown>(c, "call_brief", { p_company_id: companyId }),
      );
    },
  };
}
